import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, ArrowRight, Eye, EyeOff, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(false);
    try {
      await login(email, password);
      toast.success('Erfolgreich angemeldet');
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 403) setUnverified(true);
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await authAPI.resendVerification(email);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erneutes Senden fehlgeschlagen');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8 neon-glow">
            <Activity className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-5xl mb-4 tracking-tight">
            LAIzy<span className="text-primary">Finance</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-md mx-auto leading-relaxed">
            Intelligentes Controlling und Buchhaltung. KI-gestützt, DSGVO-konform, made for Germany.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center neon-glow">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl">
              LAIzy<span className="text-primary">Finance</span>
            </h1>
          </div>

          <div className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-2">Willkommen zurück</h2>
            <p className="text-muted-foreground">Melden Sie sich an, um fortzufahren.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">E-Mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@firma.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-background border-input focus:border-primary"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Passwort</Label>
                <Link to="/passwort-vergessen" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  Passwort vergessen?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-background border-input focus:border-primary pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-btn"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 font-bold tracking-wider uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-[0_0_15px_rgba(0,229,255,0.3)]"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          {unverified && (
            <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="unverified-notice">
              <p className="text-sm text-amber-300">
                Deine E-Mail-Adresse ist noch nicht bestätigt. Bitte prüfe dein Postfach – auch den Spam-Ordner.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={resending}
                className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="login-resend-verification-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Bestätigungs-Link erneut senden
              </Button>
            </div>
          )}

          <p className="text-center text-sm text-muted-foreground mt-8">
            Noch kein Konto?{' '}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-to-register-link">
              Jetzt registrieren
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
